import { DestroyRef, Injectable, inject } from "@angular/core";
import { BehaviorSubject } from "rxjs";

import { SessionStorageService } from "./session-storage.service";
import { ShopService } from "./shop.service";
import { BasketItemRequest } from "../dtos/basket-item.request.dto";
import { BasketResponse } from "../dtos/basket.response.dto";
import { ErrorService } from "./error.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: 'root' })
export class BasketService {
    private sessionStorageService = inject(SessionStorageService);
    private shopService = inject(ShopService);
    private errorService = inject(ErrorService);
    private destroyRef = inject(DestroyRef);

    public basketRequest: BasketItemRequest[] = [];
    public basketResponse: BasketResponse;

    private readonly _basketContentSubject = new BehaviorSubject<BasketResponse>(null);
    readonly basketContent$ = this._basketContentSubject.asObservable();

    private readonly _basketValidationErrorSubject = new BehaviorSubject<BasketResponse>(null);
    readonly basketValidationError$ = this._basketValidationErrorSubject.asObservable();

    constructor() {
        this.basketRequest = this.sessionStorageService.getBasket();

        if (!this.basketRequest)
            this.basketRequest = [];

        //this.calculateBasket();
    }

    public calculateBasket() {
        if (this.basketRequest && this.basketRequest.length > 0) {
            this.shopService.getBasketPrices(this.basketRequest)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        this.basketResponse = response as BasketResponse;

                        if (this.basketResponse.hasError)
                            this._basketValidationErrorSubject.next(this.basketResponse);

                        this._basketContentSubject.next(this.basketResponse);
                    },
                    error: (err) => {
                        this.errorService.showError(err);
                    }
                })
        }
        else {
            this.basketResponse = null;
            this._basketContentSubject.next(null);
        }
    }

    isAnyItemInBasket(): boolean {
        return this.basketRequest != null && this.basketRequest.length > 0;
    }

    isAnyItemInBasket_ByResponse(): boolean {
        return this.basketResponse != null && this.basketResponse.items.length > 0;
    }

    isAnyPayableItemInBasket(): boolean {
        return this.isAnyItemInBasket() && this.basketResponse.items.filter(ticket => !ticket.hasError).length > 0;
    }

    isBasketValid(): boolean {
        return this.isAnyItemInBasket_ByResponse() && !this.basketResponse.hasError;
    }

    public getBasket(): BasketItemRequest[] {
        return this.basketRequest;
    }

    public clearBasket() {
        this.basketRequest = [];
        this.saveBasket();
    }

    public saveBasket(): void {
        if (this.basketRequest.length > 0)
            this.sessionStorageService.saveBasket(this.basketRequest);
        else
            this.sessionStorageService.clearBasket();
    }

    public findBasketItem(ticketId: string, date: string, areaId?: string): BasketItemRequest {
        if (this.basketRequest == null || this.basketRequest == undefined)
            return null;

        return this.basketRequest.find(item =>
            (areaId != undefined ? item.areaId === areaId : true) &&
            item.ticketId === ticketId &&
            item.date === date);
    }

    //public addTicketToBasket(ticketId: string, date: string, quantity: number, areaId?: string) {
    //    this.incrementQuantity(ticketId, date, quantity, areaId);
    //}

    public incrementQuantity(ticketId: string, date: string, quantity: number, areaId?: string) {
        let foundItem = this.findBasketItem(ticketId, date, areaId);
        if (foundItem) {
            foundItem.quantity += quantity;
        }
        else {
            let item: BasketItemRequest = new BasketItemRequest();
            item.areaId = areaId;
            item.ticketId = ticketId;
            item.quantity = quantity;
            item.date = date;

            this.basketRequest.push(item);
        }

        this.saveBasket();
        this.calculateBasket();
    }

    public decrementQuantity(ticketId: string, date: string, quantity: number, areaId?: string) {
        let foundItem = this.findBasketItem(ticketId, date, areaId);

        if (foundItem) {
            if (foundItem.quantity > 1) {
                foundItem.quantity -= quantity;

                this.saveBasket();
                this.calculateBasket();
            }
        }
    }

    public removeFromBasket(ticketId: string, date: string, areaId?: string) {
        let foundItem = this.findBasketItem(ticketId, date, areaId);
        if (foundItem) {
            this.basketRequest = this.basketRequest.filter(item => !((areaId != undefined ? item.areaId === areaId : true) && item.ticketId === ticketId && item.date === date));

            this.saveBasket();
            this.calculateBasket();
        }
    }
}
